import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Logout as LogoutIcon,
  Storage as StorageIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as TaxIcon,
  TableChart as DatabaseIcon,
  CompareArrows as CompareIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  MoreHoriz as MoreHorizIcon,
  Category as CategoryIcon,
  ExpandLess,
  ExpandMore,
  Assessment as AnnualReturnsIcon,
  ShowChart as PerformanceIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import RevolutIcon from './RevolutIcon';
import VatIcon from './VatIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/ApiContext';

interface NavigationProps {
  open: boolean;
  onToggle: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const PRIMARY_PATHS = new Set([
  '/dashboard',
  '/balances-manual',
  '/personal-finances',
  '/revolut',
  '/bookings',
  '/hosting',
  '/vat',
  '/annual-returns',
  '/performance',
  '/transaction-classification-review',
  '/receipts-invoices',
]);

const Navigation: React.FC<NavigationProps> = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [otherOpen, setOtherOpen] = useState(false);
  
  // State for smooth hover expansion
  const [isHovered, setIsHovered] = useState(false);
  
  // Always show icons only on desktop, but allow smooth text expansion on hover
  const iconWidth = 64;
  const expandedWidth = 280;

  const handleNavigation = (path: string) => {
    if (path === '/revolut') {
      navigate('/revolut?tab=transactions');
    } else if (path === '/tax-returns') {
      navigate('/tax-returns?tab=vat-returns');
    } else if (path === '/receipts-invoices') {
      navigate('/receipts-invoices?tab=purchase-invoices');
    } else {
      navigate(path);
    }
    // Close drawer on mobile after navigation
    if (isMobile) {
      onToggle();
    }
  };

  const isActive = (path: string) => {
    // For Revolut, check if pathname matches (ignore query params)
    if (path === '/revolut') {
      return location.pathname === '/revolut';
    }
    // For Tax Returns, check if pathname matches (ignore query params)
    if (path === '/tax-returns') {
      return location.pathname === '/tax-returns';
    }
    // For Hosting, match /hosting and /hosting/* subpaths
    if (path === '/hosting') {
      return location.pathname === '/hosting' || location.pathname.startsWith('/hosting/');
    }
    return location.pathname === path;
  };

  // State to cache page permissions for navigation filtering
  const [pagePermissions, setPagePermissions] = useState<Record<string, boolean>>({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Fetch page permissions for current user
  useEffect(() => {
    const fetchUserPagePermissions = async () => {
      if (!user || user.role === 'admin' || user.role === 'owner') {
        // Admin and owner have access to all pages
        setPermissionsLoaded(true);
        return;
      }

      try {
        // Use the user-specific endpoint that doesn't require admin access
        const response = await api.get('/api/admin/page-permissions/my-permissions');
        const permissions = response.data.permissions || {};
        
        setPagePermissions(permissions);
        setPermissionsLoaded(true);
      } catch (err) {
        console.error('Error fetching page permissions for navigation:', err);
        // On error, hide all pages except dashboard
        setPagePermissions({ '/dashboard': true });
        setPermissionsLoaded(true);
      }
    };

    if (user && isAuthenticated) {
      fetchUserPagePermissions();
    }
  }, [user, isAuthenticated]);

  // Filter navigation items based on page permissions
  const canAccessPage = (path: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'owner') return true;
    
    // Always allow dashboard and root path (landing page)
    if (path === '/' || path === '/dashboard') return true;
    
    // For non-admin users, check cached permissions
    // If permissions haven't loaded yet, only show dashboard (prevents flash)
    if (!permissionsLoaded) {
      return path === '/dashboard';
    }
    
    // Check if user has permission for this page
    // If no permission record exists, default to false (no access)
    return pagePermissions[path] === true;
  };

  const primaryOrder = [
    '/dashboard',
    '/balances-manual',
    '/personal-finances',
    '/bookings',
    '/hosting',
    '/revolut',
    '/transaction-classification-review',
    '/receipts-invoices',
    '/vat',
    '/annual-returns',
    '/performance',
  ];

  const allCandidateItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/balances', label: 'Balance Management', icon: <AccountBalanceIcon /> },
    { path: '/balances-manual', label: 'Balances Manual', icon: <AccountBalanceIcon /> },
    { path: '/personal-finances', label: 'Personal Finances', icon: <SavingsIcon /> },
    { path: '/revolut', label: 'Revolut', icon: <RevolutIcon /> },
    { path: '/pandle-transactions', label: 'Pandle Transactions', icon: <DescriptionIcon /> },
    { path: '/receipts-invoices', label: 'Receipts & Invoices', icon: <AttachFileIcon /> },
    { path: '/bookings', label: 'Bookings', icon: <CalendarIcon /> },
    { path: '/hosting', label: 'Hosting', icon: <ReceiptIcon /> },
    { path: '/loans-schedule', label: 'Loan Amortization', icon: <TrendingUpIcon /> },
    { path: '/tax', label: 'Tax Calculations', icon: <TaxIcon /> },
    { path: '/vat', label: 'VAT', icon: <VatIcon /> },
    { path: '/annual-returns', label: 'Annual Returns', icon: <AnnualReturnsIcon /> },
    { path: '/performance', label: 'Performance', icon: <PerformanceIcon /> },
    { path: '/pensions', label: 'Pensions', icon: <TaxIcon /> },
    { path: '/tax-returns', label: 'Tax Returns', icon: <TaxIcon /> },
    { path: '/transaction-classification-review', label: 'Classification Review', icon: <CategoryIcon /> },
    { path: '/gl-transactions', label: 'General Ledger', icon: <AccountBalanceIcon /> },
    { path: '/database-overview', label: 'Database', icon: <DatabaseIcon /> },
  ];

  if (user?.role === 'admin' || user?.role === 'owner') {
    allCandidateItems.push(
      { path: '/rbac', label: 'RBAC Management', icon: <SecurityIcon /> },
      { path: '/entities', label: 'Entities', icon: <StorageIcon /> },
      { path: '/transaction-comparison', label: 'Transaction Comparison', icon: <CompareIcon /> }
    );
  }

  const accessibleItems = allCandidateItems.filter(item => canAccessPage(item.path));

  // Ensure Dashboard is always included (landing page)
  if (!accessibleItems.some(item => item.path === '/dashboard')) {
    accessibleItems.unshift({ path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> });
  }

  const primaryNavItems = primaryOrder
    .map(path => accessibleItems.find(item => item.path === path))
    .filter((item): item is NavItem => Boolean(item));

  const otherNavItems = accessibleItems.filter(item => !PRIMARY_PATHS.has(item.path));
  const otherHasActive = otherNavItems.some(item => isActive(item.path));

  useEffect(() => {
    if (otherHasActive) {
      setOtherOpen(true);
    }
  }, [location.pathname, otherHasActive]);

  const renderNavButton = (
    item: NavItem,
    options: { showIcon?: boolean; showText?: boolean; indent?: boolean; iconOnly?: boolean } = {}
  ) => {
    const { showIcon = true, showText = true, indent = false, iconOnly = false } = options;
    const button = (
      <ListItemButton
        onClick={() => handleNavigation(item.path)}
        selected={isActive(item.path)}
        sx={{
          pl: indent ? 4 : (iconOnly ? 2 : 3),
          pr: iconOnly ? 2 : 2,
          py: showText && !iconOnly ? 1.5 : 1.5,
          justifyContent: iconOnly ? 'center' : 'flex-start',
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
        }}
      >
        {showIcon && (
          <ListItemIcon
            sx={{
              minWidth: iconOnly ? 'auto' : 40,
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}
        {showText && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ variant: showIcon ? 'body1' : 'body2' }}
          />
        )}
      </ListItemButton>
    );

    if (iconOnly) {
      return (
        <ListItem key={item.path} disablePadding>
          <Tooltip title={item.label} placement="right">
            {button}
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <ListItem key={item.path} disablePadding>
        {button}
      </ListItem>
    );
  };

  const renderOtherParent = (options: {
    showIcon?: boolean;
    showText?: boolean;
    iconOnly?: boolean;
    expandIcon?: boolean;
  } = {}) => {
    const { showIcon = true, showText = true, iconOnly = false, expandIcon = true } = options;
    if (otherNavItems.length === 0) return null;

    const button = (
      <ListItemButton
        onClick={() => setOtherOpen((prev) => !prev)}
        selected={otherHasActive && !otherOpen}
        sx={{
          pl: iconOnly ? 2 : 3,
          pr: 2,
          py: 1.5,
          justifyContent: iconOnly ? 'center' : 'flex-start',
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
        }}
      >
        {showIcon && (
          <ListItemIcon
            sx={{
              minWidth: iconOnly ? 'auto' : 40,
              justifyContent: 'center',
            }}
          >
            <MoreHorizIcon />
          </ListItemIcon>
        )}
        {showText && (
          <ListItemText
            primary="Other"
            primaryTypographyProps={{ variant: showIcon ? 'body1' : 'body2' }}
          />
        )}
        {showText && expandIcon && (otherOpen ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
    );

    if (iconOnly) {
      return (
        <ListItem disablePadding>
          <Tooltip title="Other" placement="right">
            {button}
          </Tooltip>
        </ListItem>
      );
    }

    return <ListItem disablePadding>{button}</ListItem>;
  };

  const renderNavLists = (options: {
    showIcon?: boolean;
    showText?: boolean;
    iconOnly?: boolean;
    indentOther?: boolean;
  } = {}) => {
    const { showIcon = true, showText = true, iconOnly = false, indentOther = true } = options;
    return (
      <>
        {primaryNavItems.map((item) =>
          renderNavButton(item, { showIcon, showText, iconOnly })
        )}
        {renderOtherParent({ showIcon, showText, iconOnly, expandIcon: showText })}
        <Collapse in={otherOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {otherNavItems.map((item) =>
              renderNavButton(item, {
                showIcon,
                showText,
                iconOnly,
                indent: indentOther && showText && !iconOnly,
              })
            )}
          </List>
        </Collapse>
      </>
    );
  };

  // Mobile drawer content with icons and text
  const mobileDrawerContent = (
    <Box 
      sx={{ 
        width: expandedWidth, 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          Family Finance
        </Typography>
      </Box>

      {/* Navigation Items - Show icons and text on mobile */}
      <Box sx={{ flex: 1, overflow: 'auto', overflowY: 'scroll' }}>
        <List>
          {renderNavLists({ showIcon: true, showText: true, iconOnly: false })}
        </List>
      </Box>

      {/* Footer - Logout */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <ListItemButton
          onClick={() => {
            logout();
            navigate('/');
          }}
          sx={{ 
            borderRadius: 1,
            px: 2,
            py: 1.5
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  // Desktop drawer content (icon-only with hover expansion)
  const desktopDrawerContent = (
    <Box 
      sx={{ 
        width: iconWidth, 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
            FF
          </Typography>
        </Box>
      </Box>

      {/* Navigation Items - Always show icons */}
      <Box sx={{ flex: 1, overflow: 'auto', overflowY: 'scroll' }}>
        <List>
          {renderNavLists({ showIcon: true, showText: false, iconOnly: true, indentOther: false })}
        </List>
      </Box>

      {/* Footer - Always show logout icon */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
        <Tooltip title="Logout" placement="right">
          <ListItemButton
            onClick={() => {
              logout();
              navigate('/');
            }}
            sx={{ 
              borderRadius: 1,
              px: 1,
              justifyContent: 'center'
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', justifyContent: 'center' }}>
              <LogoutIcon />
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      </Box>

      {/* Smooth overlay for text expansion */}
      {isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: iconWidth,
            width: expandedWidth - iconWidth,
            height: '100%',
            backgroundColor: 'background.paper',
            borderLeft: 1,
            borderColor: 'divider',
            zIndex: 1000,
            animation: 'slideIn 0.2s ease-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header text overlay */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              Family Finance
            </Typography>
          </Box>

          {/* Navigation text overlay */}
          <Box sx={{ flex: 1, overflow: 'auto', overflowY: 'scroll', pt: 1 }}>
            <List>
              {renderNavLists({ showIcon: false, showText: true, iconOnly: false })}
            </List>
          </Box>

          {/* Footer text overlay */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <ListItemButton
              onClick={() => {
                logout();
                navigate('/');
              }}
              sx={{ 
                borderRadius: 1,
                px: 2,
                justifyContent: 'flex-start'
              }}
            >
              <ListItemText primary="Logout" />
            </ListItemButton>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {/* CSS Animation for smooth slide-in */}
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: expandedWidth,
          },
        }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* Desktop Drawer - Always visible */}
      <Drawer
        variant="persistent"
        open={true}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: iconWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: iconWidth,
            boxSizing: 'border-box',
            position: 'relative',
            zIndex: 0,
            overflow: 'visible', // Allow overlay to extend beyond
            pointerEvents: 'auto',
          },
        }}
      >
        {desktopDrawerContent}
      </Drawer>
    </>
  );
};

export default Navigation;
